import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg, formatRp, imgUrl } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUS = { draft: ["Draft", "bg-stone-100 text-stone-700"], published: ["Published", "bg-green-50 text-green-800"], archived: ["Archived", "bg-red-50 text-red-800"] };
const sel = "h-9 rounded-md border border-stone-200 bg-white px-2 text-sm";

const stockCls = (p) => (p.stock <= 0 ? "text-red-700 font-semibold" : p.stock <= (p.low_stock_threshold ?? 5) ? "text-amber-700 font-semibold" : "text-green-700");

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const exportCsv = (rows) => {
  const head = ["SKU", "Nama", "Kategori", "Harga Jual", "Diskon %", "Harga Beli", "Stok", "Status", "Tag"];
  const body = rows.map((p) => [p.sku, p.name, p.category_path, p.price, p.discount_percent, p.hpp, p.stock, p.status, p.tag_names.join("; ")].map(csvCell).join(","));
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `katalog-ellmom-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
};

const Filters = ({ f, setF, cats }) => (
  <div className="mb-4 flex flex-wrap gap-2">
    <Input placeholder="Cari nama atau SKU..." className="w-64 bg-white" value={f.q} onChange={(e) => setF({ q: e.target.value, page: 1 })} data-testid="admin-product-search" />
    <select className={sel} value={f.category} onChange={(e) => setF({ category: e.target.value, page: 1 })} data-testid="filter-category">
      <option value="">Semua kategori</option>
      {cats.map((c) => <option key={c.id} value={c.id}>{c.parent_id ? "\u00a0\u00a0\u203a " : ""}{c.name}</option>)}
    </select>
    <select className={sel} value={f.status} onChange={(e) => setF({ status: e.target.value, page: 1 })} data-testid="filter-status">
      <option value="">Semua status</option><option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
    </select>
    <select className={sel} value={f.stock} onChange={(e) => setF({ stock: e.target.value, page: 1 })} data-testid="filter-stock">
      <option value="">Semua stok</option><option value="low">Stok menipis</option><option value="out">Stok habis</option>
    </select>
    <select className={sel} value={`${f.sort}:${f.order}`} onChange={(e) => { const [sort, order] = e.target.value.split(":"); setF({ sort, order }); }} data-testid="sort-select">
      <option value="created_at:desc">Terbaru</option><option value="created_at:asc">Terlama</option><option value="name:asc">Nama A-Z</option><option value="name:desc">Nama Z-A</option>
      <option value="price:asc">Harga termurah</option><option value="price:desc">Harga termahal</option><option value="stock:asc">Stok tersedikit</option><option value="stock:desc">Stok terbanyak</option>
    </select>
  </div>
);

const BulkBar = ({ count, onStatus, onDelete, onExport }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm" data-testid="bulk-bar">
    <span className="px-2 font-medium">{count} dipilih</span>
    <select className={sel} defaultValue="" onChange={(e) => { if (e.target.value) onStatus(e.target.value); e.target.value = ""; }} data-testid="bulk-status-select">
      <option value="">Ubah status...</option><option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
    </select>
    <Button size="sm" variant="outline" onClick={onExport} data-testid="bulk-export-button"><Download className="mr-1 h-4 w-4" />Export CSV</Button>
    <Button size="sm" variant="outline" className="text-red-700" onClick={onDelete} data-testid="bulk-delete-button"><Trash2 className="mr-1 h-4 w-4" />Hapus</Button>
  </div>
);

const Row = ({ p, checked, onCheck, onDelete }) => (
  <tr data-testid={`admin-product-row-${p.id}`}>
    <td className="p-3"><input type="checkbox" checked={checked} onChange={onCheck} className="accent-amber-700" data-testid={`select-product-${p.id}`} /></td>
    <td className="p-3"><img src={imgUrl(p.images?.[0])} alt="" className="h-10 w-10 rounded object-cover" /></td>
    <td className="p-3 font-medium">{p.name}</td>
    <td className="p-3 font-mono text-xs">{p.sku}</td>
    <td className="p-3 text-stone-600">{p.category_path}</td>
    <td className="tnum p-3 text-right">{formatRp(p.final_price)}{p.discount_percent > 0 && <span className="block text-xs text-stone-400">-{p.discount_percent}%</span>}</td>
    <td className={`tnum p-3 text-right ${stockCls(p)}`}>{p.stock}</td>
    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[p.status]?.[1]}`}>{STATUS[p.status]?.[0] || p.status}</span></td>
    <td className="whitespace-nowrap p-3 text-right">
      <Button asChild variant="outline" size="sm" className="mr-1" data-testid={`edit-product-${p.id}`}><Link to={`/admin/produk/${p.id}`}><Pencil className="mr-1 h-4 w-4" />Edit</Link></Button>
      <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800" data-testid={`delete-product-${p.id}`} onClick={onDelete}><Trash2 className="mr-1 h-4 w-4" />Hapus</Button>
    </td>
  </tr>
);

const Pager = ({ f, setF, total }) => {
  const pages = Math.max(1, Math.ceil(total / f.page_size));
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-stone-600" data-testid="product-pager">
      <span>{total} produk</span>
      <select className={sel} value={f.page_size} onChange={(e) => setF({ page_size: Number(e.target.value), page: 1 })} data-testid="page-size-select">
        {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / halaman</option>)}
      </select>
      <div className="ml-auto flex items-center gap-2">
        <Button size="icon" variant="outline" disabled={f.page <= 1} onClick={() => setF({ page: f.page - 1 })} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span data-testid="page-indicator">Hal. {f.page} / {pages}</span>
        <Button size="icon" variant="outline" disabled={f.page >= pages} onClick={() => setF({ page: f.page + 1 })} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export default function AdminProducts() {
  const call = useApi();
  const [f, setFState] = useState({ q: "", category: "", status: "", stock: "", sort: "created_at", order: "desc", page: 1, page_size: 25 });
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [picked, setPicked] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const setF = (patch) => setFState((s) => ({ ...s, ...patch }));

  const params = useCallback((extra = {}) => Object.fromEntries(Object.entries({ ...f, ...extra }).filter(([, v]) => v !== "")), [f]);
  const load = useCallback(() => call("get", "/admin/products", undefined, { params: params() }).then(setData), [call, params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { setPicked([]); }, [f]);

  const bulk = async (body, msg) => {
    try { await call("post", "/admin/products/bulk", body); toast.success(msg); setPicked([]); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const doDelete = async () => {
    const ids = confirm.ids;
    setConfirm(null);
    await bulk({ ids, action: "delete" }, ids.length > 1 ? `${ids.length} produk dihapus` : "Produk dihapus");
  };
  const exportAll = async () => {
    const rows = picked.length ? data.items.filter((p) => picked.includes(p.id))
      : (await call("get", "/admin/products", undefined, { params: params({ page: 1, page_size: 1000 }) })).items;
    exportCsv(rows);
  };
  const allChecked = data.items.length > 0 && data.items.every((p) => picked.includes(p.id));

  return (
    <div>
      <PageHeader title="Katalog Produk">
        <Button variant="outline" onClick={exportAll} data-testid="export-products-button"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
        <Button asChild data-testid="add-product-button"><Link to="/admin/produk/baru"><Plus className="mr-1 h-4 w-4" /> Tambah Produk</Link></Button>
      </PageHeader>
      <Filters f={f} setF={setF} cats={cats} />
      {picked.length > 0 && <BulkBar count={picked.length} onExport={exportAll} onDelete={() => setConfirm({ ids: picked, label: `${picked.length} produk terpilih` })}
        onStatus={(status) => bulk({ ids: picked, action: "status", status }, "Status diperbarui")} />}
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full min-w-[920px] text-sm">
          <thead className="bg-stone-100 text-left text-xs text-stone-600">
            <tr><th className="p-3"><input type="checkbox" className="accent-amber-700" checked={allChecked} onChange={() => setPicked(allChecked ? [] : data.items.map((p) => p.id))} data-testid="select-all-products" /></th>
              <th className="p-3">Foto</th><th className="p-3">Nama</th><th className="p-3">SKU</th><th className="p-3">Kategori</th><th className="p-3 text-right">Harga</th><th className="p-3 text-right">Stok</th><th className="p-3">Status</th><th className="p-3 text-right">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {data.items.map((p) => <Row key={p.id} p={p} checked={picked.includes(p.id)} onCheck={() => setPicked((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]))} onDelete={() => setConfirm({ ids: [p.id], label: `"${p.name}"` })} />)}
          </tbody>
        </table>
        {data.items.length === 0 && <div className="p-10 text-center text-sm text-stone-500" data-testid="products-empty">Tidak ada produk yang cocok. <Link to="/admin/produk/baru" className="text-amber-700 underline">Tambah produk baru</Link></div>}
      </div>
      <Pager f={f} setF={setF} total={data.total} />
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus produk?</AlertDialogTitle><AlertDialogDescription>{confirm?.label} akan dihapus dari katalog. Riwayat pesanan tetap tersimpan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-product" onClick={doDelete}>Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
